import { Link } from 'react-router-dom';

import { Button } from '@repo/ui/button';

import { AlertIcon, ArrowLeftIcon, LockIcon, ReloadIcon } from '../lib/icons';
import type { EndReason, FailureKind } from './use-browser-session';

/**
 * The two screens that replace the browser entirely: a session that failed to
 * start (or died), and one that ended cleanly.
 *
 * Each failure kind gets its own sentence and its own primary action, because
 * they are not the same problem — "every instance is busy" is worth retrying
 * this second, "your session was rejected" is not, and a crash means the page
 * is fine but the browser isn't. A single generic "Something went wrong" would
 * make all three look like our fault and give the user nothing to do.
 */

interface Copy {
  title: string;
  body: string;
  /** Null when retrying can't plausibly help. */
  retryLabel: string | null;
}

const FAILURE_COPY: Record<FailureKind, Copy> = {
  busy: {
    body: 'Every browser instance is currently in use. They free up quickly — trying again usually lands on a fresh one.',
    retryLabel: 'Try again',
    title: 'No browser free right now',
  },
  crashed: {
    body: 'The browser stopped responding and the session was lost. Your captures so far are still on the canvas.',
    retryLabel: 'Restart the browser',
    title: 'The browser session dropped',
  },
  unauthorized: {
    body: "This browser session wasn't accepted. Signing out and back in refreshes the credentials it uses.",
    retryLabel: null,
    title: 'Session not authorized',
  },
  unreachable: {
    body: "We couldn't reach the browser service. It may be starting up, or your network is blocking the connection.",
    retryLabel: 'Try again',
    title: "Couldn't reach the browser service",
  },
};

const END_COPY: Record<EndReason, Copy> = {
  closed: {
    body: 'The browser was shut down. Start it again to keep going where you left off.',
    retryLabel: 'Start it again',
    title: 'Browser closed',
  },
  idle: {
    body: 'Browsers are released after a few minutes without activity so they can be given to someone else.',
    retryLabel: 'Start the browser again',
    title: 'Browser timed out',
  },
};

function StatusScreen({
  captureCount,
  copy,
  icon,
  onRetry,
  tone,
}: {
  captureCount: number;
  copy: Copy;
  icon: 'alert' | 'lock';
  onRetry: () => void;
  tone: 'critical' | 'neutral';
}) {
  return (
    <div className="flex min-h-svh flex-col items-center justify-center bg-warm px-6">
      <div className="w-full max-w-[440px] duration-300 animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none">
        <span
          className={[
            'flex size-11 items-center justify-center rounded-card border',
            tone === 'critical'
              ? 'border-critical/20 bg-tag-critical text-critical'
              : 'border-hairline bg-surface text-text-secondary',
          ].join(' ')}
        >
          {icon === 'lock' ? (
            <LockIcon className="size-5" />
          ) : (
            <AlertIcon className="size-5" />
          )}
        </span>

        <h1 className="!mt-6 !mb-0 !text-page-title !font-semibold !tracking-tight !text-text-primary">
          {copy.title}
        </h1>
        <p className="mt-2 text-body text-text-secondary">{copy.body}</p>

        {captureCount > 0 ? (
          <p className="mt-4 inline-flex items-center gap-2 rounded-pill bg-surface px-3 py-1.5 text-ui text-text-secondary ring-1 ring-hairline">
            {captureCount} capture{captureCount === 1 ? '' : 's'} kept
          </p>
        ) : null}

        <div className="mt-8 flex items-center gap-3">
          {copy.retryLabel ? (
            <Button
              className="h-11 cursor-pointer rounded-button bg-ink px-5 text-ui-lg font-semibold text-white hover:bg-ink/90"
              onClick={onRetry}
              type="button"
            >
              <ReloadIcon className="size-[18px] text-highlight" />
              {copy.retryLabel}
            </Button>
          ) : null}

          {/* A plain styled Link, not <Button render={...}> — the route change
              is the whole job and a real anchor keeps middle-click/open-in-new-tab. */}
          <Link
            className="inline-flex h-11 items-center gap-2 rounded-button border border-hairline-alt bg-surface px-5 text-ui-lg font-medium text-text-primary transition hover:bg-surface-hover active:scale-[0.98]"
            to="/"
          >
            <ArrowLeftIcon className="size-[18px]" />
            Back to import
          </Link>
        </div>
      </div>
    </div>
  );
}

export function SessionFailedView({
  captureCount,
  kind,
  onRetry,
}: {
  captureCount: number;
  kind: FailureKind;
  onRetry: () => void;
}) {
  return (
    <StatusScreen
      captureCount={captureCount}
      copy={FAILURE_COPY[kind]}
      icon={kind === 'unauthorized' ? 'lock' : 'alert'}
      onRetry={onRetry}
      tone="critical"
    />
  );
}

export function SessionEndedView({
  captureCount,
  onRetry,
  reason,
}: {
  captureCount: number;
  onRetry: () => void;
  reason: EndReason;
}) {
  return (
    <StatusScreen
      captureCount={captureCount}
      copy={END_COPY[reason]}
      icon="alert"
      onRetry={onRetry}
      tone="neutral"
    />
  );
}
